import React, { useMemo, useState, useEffect } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { inventoryTransformationService, type InventoryTransformationDetail } from '@services/operator/inventoryTransformationService';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { Button, Card, Form, Input, message, Space, Spin, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';

import { getTransformationItemColumns } from './columns';
import TransformationItemModal from './components/TransformationItemModal';
import { normalizeSelectorInventory } from './utils';
import { toDayjsOrNull } from '@utils/dateTime';

const { Title } = Typography;

// item-row 形狀對齊 columns.jsx 的 dataIndex 與 TransformationItemModal onConfirm 拿出來的
// payload；columns / modal 都是 .jsx 沒型別，這裡先用 record-like 形態，必要欄位顯式列出。
interface TransformationItemRow {
  tempId: string | number;
  inventory_id: string | null;
  shelf_id: string | null;
  quantity: number;
  reason_code?: string;
  target_batch?: string | null;
  target_expiration_date?: unknown;
  target_manufacturing_date?: unknown;
  target_inventory_type_id?: string | null;
  target_inventory_type_name?: string | null;
  target_custom_attributes?: Record<string, unknown> | null;
  note?: string | null;
  inventory: Record<string, unknown>;
  shelf: Record<string, unknown>;
  [key: string]: unknown;
}

export default function InventoryTransformationEdit(): React.ReactElement {
  // Loader (operator menuConfig.tsx#createLoader) fetches GET /inventory_transformations/:id
  // and returns response.data. After the merchant_id + nested merchant addition this
  // payload now carries the canonical merchant-scope keys.
  const transformation = useLoaderData() as InventoryTransformationDetail;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();

  // useRecordMerchantSync 對齊 OperatorDataContext.currentMerchant 到 record 的 merchant。
  // 沒有這個 sync，operator 在 header 切到 merchant B 後打開屬於 merchant A 的 transformation
  // 編輯頁時：屬性定義 / 商品搜尋下拉會跑出 B 的選項，存檔會把 B 的 id 寫進 A 的 record。
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();

  // 一拿到 loader 結果就觸發 sync — useEffect 而不是直接呼叫，避免 render-phase setState。
  useEffect(() => {
    handleRecordLoaded(transformation);
  }, [transformation, handleRecordLoaded]);

  // Reference data 一律跟著 record's merchant_id（非 header 的 currentMerchant）。
  // sync 完成前 merchantDataLoading 為 true，由 <Spin> 擋住整個表單，避免閃過舊 merchant 的選項。
  const recordMerchantId = transformation.merchant_id;

  const productService = useOperatorProductService({ merchantId: recordMerchantId });
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', recordMerchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  );

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<TransformationItemRow[]>([]);

  const [selectorVisible, setSelectorVisible] = useState<boolean>(false);
  const [itemModalVisible, setItemModalVisible] = useState<boolean>(false);
  const [selectedInventory, setSelectedInventory] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!transformation) return;
    form.setFieldsValue({
      name: transformation.name,
      note: transformation.note,
    });

    const mappedItems: TransformationItemRow[] = transformation.items.map(item => ({
      tempId: item.id,
      inventory_id: item.source_inventory_id,
      shelf_id: item.shelf_id,
      quantity: item.quantity,
      reason_code: item.reason_code,
      target_batch: item.target_batch,
      target_expiration_date: toDayjsOrNull(item.target_expiration_date),
      target_manufacturing_date: toDayjsOrNull(item.target_manufacturing_date),
      target_inventory_type_id: item.target_inventory_type_id,
      target_inventory_type_name: item.target_inventory_type_name,
      target_custom_attributes: item.target_custom_attributes,
      note: item.note,
      // columns.jsx 讀的是巢狀 inventory.product.{id,sku,name} 與 inventory.custom_attributes
      // （對齊 New 頁 InventorySelectorModal 給的 shape）。後端 show payload 是 flat 欄位，
      // 這裡要 reshape 成巢狀，否則品名欄與自訂屬性來源側會取到 undefined 而整欄空白。
      inventory: {
        id: item.source_inventory_id,
        product: {
          id: item.product_id,
          sku: item.product_sku,
          name: item.product_name,
        },
        batch: item.source_batch,
        expiration_date: item.source_expiration_date,
        manufacturing_date: item.source_manufacturing_date,
        inventory_type: item.source_inventory_type_name,
        inventory_type_id: item.source_inventory_type_id,
        custom_attributes: item.source_custom_attributes,
      },
      shelf: {
        shelf_id: item.shelf_id,
        shelf_location: item.shelf_location,
        shelf_code: item.shelf_location,
      },
    }));

    setItems(mappedItems);
  }, [transformation, form]);

  // InventorySelectorModal.onSelect 給 InventoryRecord（非 Inventory），這裡走 normalize
  // 把 inventory_type 物件壓成字串、把 inventory_type_id 拉出來，對齊
  // TransformationItemModal 的 Inventory shape。`as never` 故意避開 modal 的 Inventory
  // narrow check — modal 自己內部會用到所有需要的欄位，型別差異是名字不同而已。
  const handleSelectInventory = (inventory: Parameters<typeof normalizeSelectorInventory>[0]): void => {
    setSelectedInventory(normalizeSelectorInventory(inventory));
    setSelectorVisible(false);
    setItemModalVisible(true);
  };

  const handleConfirmItem = (itemDataOrArray: Record<string, unknown> | Array<Record<string, unknown>>): void => {
    const newItems = Array.isArray(itemDataOrArray) ? itemDataOrArray : [itemDataOrArray];
    const now = Date.now();
    setItems(prev => [
      ...prev,
      ...newItems.map((item, idx) => ({
        ...(item as TransformationItemRow),
        tempId: `${now}-${idx}`,
      })),
    ]);
    setItemModalVisible(false);
    setSelectedInventory(null);
  };

  const handleRemoveItem = (index: number): void => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleNoteChange = (index: number, value: string): void => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, note: value } : item)));
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      if (items.length === 0) {
        message.error(t('shared.inventory_transformation.error.no_items'));
        return;
      }

      setLoading(true);
      const payload = {
        ...values,
        items: items.map(item => ({
          inventory_id: item.inventory_id,
          shelf_id: item.shelf_id,
          quantity: item.quantity,
          reason_code: item.reason_code,
          target_batch: item.target_batch,
          target_expiration_date: item.target_expiration_date,
          target_manufacturing_date: item.target_manufacturing_date,
          target_inventory_type_id: item.target_inventory_type_id,
          target_custom_attributes: item.target_custom_attributes,
          note: item.note,
        })),
      };

      const response = await inventoryTransformationService.update(transformation.id, payload);
      if (response.success) {
        message.success(t('common.message.update_success'));
        navigate('/inventory_transformations');
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.message.operation_failed') });
      }
    } catch (error) {
      console.error('[InventoryTransformationsEdit] submit failed:', error);
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const columns = getTransformationItemColumns(t, {
    onRemove: handleRemoveItem,
    onNoteChange: handleNoteChange,
    attributeLabelByName,
  });

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        items={[ 'stocks', 'stocks.transformations', { label: transformation?.name || t('shared.inventory_transformation.edit_title') } ]}
      />

      <Spin spinning={merchantDataLoading}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>{t('shared.inventory_transformation.edit_title')}</Title>
          <Space>
            <Button onClick={goBack}>{t('common.button.cancel')}</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {t('common.button.save')}
            </Button>
          </Space>
        </div>

        <Card title={t('shared.inventory_transformation.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
          <Form form={form} layout="inline">
            <Form.Item label={t('models.inventory_transformation.name')} name="name">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item label={t('models.inventory_transformation.note')} name="note" style={{ flex: 1 }}>
              <Input.TextArea rows={1} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Card>

        <Card
          title={t('shared.inventory_transformation.items')}
          bordered={false}
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setSelectorVisible(true)}>
              {t('common.button.add')}
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={items}
            rowKey="tempId"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('shared.inventory_transformation.no_items') }}
          />
        </Card>

        <InventorySelectorModal
          visible={selectorVisible}
          onCancel={() => setSelectorVisible(false)}
          onSelect={handleSelectInventory}
          merchantId={recordMerchantId}
          productService={productService}
        />

        {/* TransformationItemModal 的 Inventory interface 有 `id: string` required；
            normalizeSelectorInventory 的回傳保留原 input 形狀，TS 推不到 `id` 必存在。
            runtime 上 InventorySelectorModal.onSelect 拿到的 inventory 一定有 id（query
            就是用 id 抓的），cast 後不影響行為。 */}
        <TransformationItemModal
          visible={itemModalVisible}
          inventory={selectedInventory as React.ComponentProps<typeof TransformationItemModal>['inventory']}
          existingItems={items as unknown as React.ComponentProps<typeof TransformationItemModal>['existingItems']}
          defaultReasonCode={items.length > 0 ? (items[items.length - 1].reason_code as string | undefined) : undefined}
          onCancel={() => setItemModalVisible(false)}
          onConfirm={handleConfirmItem}
        />
      </Spin>
    </div>
  );
}
